import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Cookie, X } from "lucide-react";
import { useI18n } from "../context/I18nContext";
import { loadAnalytics } from "../lib/analytics";

const LS_KEY = "cs_cookie_consent";

function readConsent() {
  try {
    return JSON.parse(localStorage.getItem(LS_KEY));
  } catch {
    return null;
  }
}

export default function CookieConsent() {
  const { t } = useI18n();
  const [visible, setVisible] = useState(false);
  const [configOpen, setConfigOpen] = useState(false);
  const [analytics, setAnalytics] = useState(false);

  const apply = (consent) => {
    if (consent && consent.analytics) loadAnalytics();
  };

  useEffect(() => {
    const stored = readConsent();
    if (stored) {
      apply(stored);
      return;
    }
    setVisible(true);
  }, []);

  useEffect(() => {
    const open = () => {
      setConfigOpen(true);
      setVisible(true);
      setAnalytics(!!readConsent()?.analytics);
    };
    window.addEventListener("cs-open-cookie-settings", open);
    return () => window.removeEventListener("cs-open-cookie-settings", open);
  }, []);

  const save = (value) => {
    localStorage.setItem(LS_KEY, JSON.stringify({ analytics: value, ts: new Date().toISOString() }));
    setVisible(false);
    setConfigOpen(false);
    if (value) {
      loadAnalytics();
    } else {
      document.cookie.split(";").forEach((c) => {
        const name = c.split("=")[0].trim();
        if (name.startsWith("ph_")) {
          document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
        }
      });
      if (window.posthog && window.posthog.opt_out_capturing) window.posthog.opt_out_capturing();
    }
  };

  const ui = t.cookiesUI;

  return (
    <>
      <AnimatePresence>
        {visible && !configOpen && (
          <motion.div
            data-testid="cookie-banner"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 24 }}
            transition={{ duration: 0.4 }}
            className="fixed bottom-4 left-4 right-4 z-[80] border border-white/15 bg-charcoal/95 p-6 text-cream shadow-2xl backdrop-blur-md sm:max-w-sm"
          >
            <div className="flex items-start justify-between gap-3">
              <p className="inline-flex items-center gap-2 font-serif text-lg">
                <Cookie size={17} className="text-terra" />
                {ui.title}
              </p>
              <button
                data-testid="cookie-reject-button"
                onClick={() => save(false)}
                className="p-1 text-cream/50 transition-colors hover:text-cream"
                aria-label={ui.reject}
              >
                <X size={16} />
              </button>
            </div>
            <p className="mt-3 text-xs leading-relaxed text-cream/70">{ui.text}</p>
            <div className="mt-5 flex flex-wrap items-center gap-2">
              <button
                data-testid="cookie-accept-button"
                onClick={() => save(true)}
                className="rounded-full bg-terra px-5 py-2 text-xs font-medium text-cream transition-colors hover:bg-terra-dark"
              >
                {ui.accept}
              </button>
              <button
                data-testid="cookie-reject-all-button"
                onClick={() => save(false)}
                className="rounded-full border border-cream/30 px-5 py-2 text-xs font-medium text-cream transition-colors hover:border-cream"
              >
                {ui.reject}
              </button>
              <button
                data-testid="cookie-config-button"
                onClick={() => setConfigOpen(true)}
                className="px-3 py-2 text-xs text-cream/60 underline underline-offset-4 transition-colors hover:text-cream"
              >
                {ui.config}
              </button>
            </div>
          </motion.div>
        )}

        {configOpen && (
          <motion.div
            data-testid="cookie-config-panel"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 24 }}
            transition={{ duration: 0.4 }}
            className="fixed bottom-4 left-4 right-4 z-[80] max-h-[85vh] overflow-y-auto border border-white/15 bg-charcoal/95 p-6 text-cream shadow-2xl backdrop-blur-md sm:left-auto sm:max-w-md"
          >
            <div className="flex items-start justify-between gap-3">
              <p className="font-serif text-lg">{ui.title}</p>
              <button
                data-testid="cookie-config-close"
                onClick={() => setConfigOpen(false)}
                className="p-1 text-cream/50 transition-colors hover:text-cream"
                aria-label="Close"
              >
                <X size={16} />
              </button>
            </div>

            <div className="mt-5 space-y-4">
              <div className="border border-white/10 bg-white/5 p-4">
                <div className="flex items-center justify-between gap-3">
                  <p className="text-sm font-medium">{ui.necessary}</p>
                  <span className="rounded-full bg-olive/40 px-3 py-1 text-[10px] uppercase tracking-widest text-olivelight">
                    ✓
                  </span>
                </div>
                <p className="mt-1.5 text-xs leading-relaxed text-cream/60">{ui.necessaryDesc}</p>
              </div>

              <button
                type="button"
                data-testid="cookie-analytics-toggle"
                onClick={() => setAnalytics(!analytics)}
                className={`block w-full border p-4 text-left transition-colors ${
                  analytics ? "border-terra bg-terra/10" : "border-white/10 bg-white/5 hover:border-white/25"
                }`}
              >
                <span className="flex items-center justify-between gap-3">
                  <span className="text-sm font-medium">{ui.analytics}</span>
                  <span
                    className={`inline-flex h-5 w-9 items-center rounded-full px-0.5 transition-colors ${
                      analytics ? "justify-end bg-terra" : "justify-start bg-white/20"
                    }`}
                  >
                    <span className="h-4 w-4 rounded-full bg-cream" />
                  </span>
                </span>
                <span className="mt-1.5 block text-xs leading-relaxed text-cream/60">{ui.analyticsDesc}</span>
              </button>
            </div>

            <button
              data-testid="cookie-save-button"
              onClick={() => save(analytics)}
              className="mt-6 w-full rounded-full bg-terra py-3 text-sm font-medium text-cream transition-colors hover:bg-terra-dark"
            >
              {ui.save}
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
